/**
 * User-layer runtime settings.
 *
 * SkaileSettings represents the personal, machine-specific overrides stored in
 * .skaile/settings.json (gitignored). Project-level defaults (framework, agent
 * definition, resource connections, hooks) belong in skaile.yaml via SkWorkspaceConfig.
 *
 * Resolution order (highest priority first):
 *   1. CLI flags / API overrides
 *   2. Environment variables (API keys only)
 *   3. .skaile/settings.json  (project-local, walking upward)
 *   4. ~/.skaile/settings.json (user global)
 *   5. skaile.yaml defaults:  (via resolveSkWorkspaceConfig — no duplicate YAML parser)
 *   6. Built-in SETTINGS_DEFAULTS
 */

import fs from "node:fs";
import fsp from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import {
  cloudConfigFromProfile,
  type CloudTransportConfig,
  resolveSkWorkspaceConfig,
} from "./workspace-config.js";

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * All supported LLM and voice provider identifiers.
 * @docLink packages/core/settings#all-providers
 */
export const ALL_PROVIDERS = [
  "anthropic",
  "openai",
  "google",
  "mistral",
  "groq",
  "openrouter",
  "deepseek",
  "xai",
  "together",
  "fireworks",
  "deepgram",
  "elevenlabs",
] as const;

/**
 * Union of supported LLM and voice provider identifiers.
 * @docLink packages/core/settings#provider
 */
export type Provider = (typeof ALL_PROVIDERS)[number];

/**
 * Personal runtime settings — stored in .skaile/settings.json (gitignored).
 * Contains only user-specific overrides: credentials, preferred model/driver.
 *
 * Project-level config (framework, resources, hooks, agent definition) lives
 * in skaile.yaml as SkWorkspaceConfig. App-specific fields (voice, domains)
 * are added by forge-common-backend via AppSettings.
 * @docLink packages/core/settings#skaile-settings
 */
export interface SkaileSettings {
  /** Active LLM provider identifier (e.g. "anthropic", "openai"). */
  provider?: string;
  /** Active model identifier (e.g. "claude-opus-5"). */
  model?: string;
  /** Bridge driver to use: "omp", "claude-sdk", or "codex". */
  driver?: string;
  /**
   * AI cloud transport for claude-sdk (`default|bedrock|vertex|azure|gateway`).
   * Absent = default (provider's native API). Sourced from skaile.yaml
   * `agent-config.<profile>.cloud`; string-typed for forward compat.
   *
   * @since 1.3.0
   */
  cloud?: string;
  /**
   * Non-secret cloud transport settings (camelCase; converted once from the
   * skaile.yaml `cloud_config` snake_case wire shape).
   *
   * @since 1.3.0
   */
  cloudConfig?: CloudTransportConfig;
  /** API keys keyed by provider identifier. Merged with env-var keys at resolution time. */
  apiKeys?: Record<string, string>;
  /** Framework install path overrides. Normally derived from skaile.yaml defaults. */
  skillsDir?: string;
  /** Override for the agents directory. Normally derived from skaile.yaml defaults. */
  agentsDir?: string;
  /** Override for the prompts directory. Normally derived from skaile.yaml defaults. */
  promptsDir?: string;
  /** AI Asset Store API URL (default: https://store.skaile.ai) */
  storeUrl?: string;
  /** JWT access token for the AI Asset Store */
  storeJwt?: string;
  /** Refresh token for JWT renewal */
  storeRefreshToken?: string;
}

/**
 * Built-in default settings. Lowest-priority layer in resolveSettings().
 * Override via .skaile/settings.json, ~/.skaile/settings.json, or env vars.
 *
 * `model` is deliberately absent. A hardcoded default here is not a floor a
 * consumer can opt out of: because `loadSettings` and `resolveSettings` spread
 * these defaults *under* every other layer, deleting `model` from a settings
 * file does not unset it — the built-in comes back. A caller therefore cannot
 * express "no model configured" and cannot route an unconfigured session to its
 * own per-backend default; it silently inherits whichever id was pinned here,
 * however stale. Leaving it unset lets each driver apply its own current
 * default (omp omits `--model`; claude-sdk falls back to the `sonnet` rolling
 * alias), which is the one that ages correctly.
 *
 * @docLink packages/core/settings#settings-defaults
 */
export const SETTINGS_DEFAULTS: SkaileSettings = {
  apiKeys: {},
  driver: "omp",
  provider: "anthropic",
};

// ── Legacy field mapping ───────────────────────────────────────────────────

const LEGACY_FIELD_MAP: Record<string, keyof SkaileSettings> = {
  defaultProvider: "provider",
  defaultModel: "model",
  defaultAgentType: "driver",
};

/**
 * Rename legacy field names in a raw parsed settings object to their current equivalents.
 * Maps `defaultProvider` → `provider`, `defaultModel` → `model`, `defaultAgentType` → `driver`.
 *
 * @param raw - Raw object from JSON.parse of a settings file
 * @returns Settings with legacy keys renamed to current ones
 * @docLink packages/core/settings#map-legacy-fields
 */
export function mapLegacyFields(raw: Record<string, unknown>): SkaileSettings {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    const mapped = LEGACY_FIELD_MAP[key];
    if (mapped) {
      if (!(mapped in result) || result[mapped] == null) result[mapped] = value;
    } else {
      result[key] = value;
    }
  }
  return result as SkaileSettings;
}

// ── Paths ──────────────────────────────────────────────────────────────────

const SKAILE_DIR = ".skaile";
const SETTINGS_FILE = "settings.json";

/**
 * Return the path to the user-global settings file: `~/.skaile/settings.json`.
 * @docLink packages/core/settings#global-settings-path
 */
export function globalSettingsPath(): string {
  return path.join(os.homedir(), SKAILE_DIR, SETTINGS_FILE);
}

/**
 * Return the path to the project-local settings file: `<projectDir>/.skaile/settings.json`.
 *
 * @param projectDir - Absolute path to the project root
 * @returns Absolute path to the project settings file
 * @docLink packages/core/settings#project-settings-path
 */
export function projectSettingsPath(projectDir: string): string {
  return path.join(projectDir, SKAILE_DIR, SETTINGS_FILE);
}

// ── I/O ────────────────────────────────────────────────────────────────────

/**
 * Load and parse a settings JSON file, merging over SETTINGS_DEFAULTS.
 * Returns defaults if the file does not exist or cannot be parsed.
 *
 * @param filePath - Absolute path to a settings.json file
 * @returns Parsed settings merged with built-in defaults
 * @docLink packages/core/settings#load-settings
 */
export async function loadSettings(filePath: string): Promise<SkaileSettings> {
  try {
    const raw = await fsp.readFile(filePath, "utf-8");
    return { ...SETTINGS_DEFAULTS, ...mapLegacyFields(JSON.parse(raw)) };
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
}

/**
 * Persist settings to a JSON file, creating parent directories as needed.
 *
 * @param settings - Settings object to serialize
 * @param filePath - Absolute path to write the settings.json file
 * @docLink packages/core/settings#save-settings
 */
export async function saveSettings(settings: SkaileSettings, filePath: string): Promise<void> {
  await fsp.mkdir(path.dirname(filePath), { recursive: true });
  await fsp.writeFile(filePath, JSON.stringify(settings, null, 2), "utf-8");
}

/**
 * Walk upward from `projectDir` (max 10 levels) and collect all
 * `.skaile/settings.json` paths found along the way.
 * Results are ordered nearest-to-root first.
 *
 * @param projectDir - Starting directory for the upward walk
 * @returns Array of absolute paths to settings files found
 * @docLink packages/core/settings#walk-for-settings
 */
export function walkForSettings(projectDir: string): string[] {
  const found: string[] = [];
  let dir = path.resolve(projectDir);
  const root = path.parse(dir).root;
  for (let i = 0; i < 10; i++) {
    const candidate = path.join(dir, SKAILE_DIR, SETTINGS_FILE);
    if (fs.existsSync(candidate)) found.push(candidate);
    const parent = path.dirname(dir);
    if (parent === dir || parent === root) break;
    dir = parent;
  }
  return found;
}

// ── Resolution ─────────────────────────────────────────────────────────────

function mergeSettings(base: SkaileSettings, source: Partial<SkaileSettings>): SkaileSettings {
  const result = { ...base };
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined && value !== null && value !== "") {
      if (key === "apiKeys" && typeof value === "object") {
        result.apiKeys = { ...result.apiKeys, ...(value as Record<string, string>) };
      } else {
        (result as any)[key] = value;
      }
    }
  }
  return result;
}

/** Derive a SkaileSettings-compatible layer from SkWorkspaceConfig agent_config.default. */
function settingsFromWorkspaceConfig(projectDir: string): SkaileSettings {
  try {
    const ws = resolveSkWorkspaceConfig(projectDir);
    // Read agent_config.default
    const p = ws.agent_config?.default;
    if (!p) return {};
    const result: SkaileSettings = {};
    if (p.driver) result.driver = p.driver;
    if (p.provider) result.provider = p.provider;
    if (p.cloud) result.cloud = p.cloud;
    const cloudConfig = cloudConfigFromProfile(p);
    if (cloudConfig) result.cloudConfig = cloudConfig;
    if (p.model) result.model = p.model;
    if (p.skills_dir) result.skillsDir = p.skills_dir;
    if (p.agents_dir) result.agentsDir = p.agents_dir;
    if (p.prompts_dir) result.promptsDir = p.prompts_dir;
    return result;
  } catch {
    return {};
  }
}

// ── Migration ──────────────────────────────────────────────────────────────

const _migrated = new Set<string>();

/**
 * One-time migration from old config locations to .skaile/settings.json.
 * Does not delete old files — user can do that manually.
 * @docLink packages/core/settings#migrate-settings
 */
export async function migrateSettings(projectDir: string): Promise<void> {
  const resolved = path.resolve(projectDir);
  if (_migrated.has(resolved)) return;

  const targetPath = projectSettingsPath(resolved);
  if (fs.existsSync(targetPath)) {
    _migrated.add(resolved);
    return;
  }

  const sources: Record<string, unknown>[] = [];

  // Old location 1: data/settings.json (forge-project)
  const dataSettings = path.join(resolved, "data", "settings.json");
  if (fs.existsSync(dataSettings)) {
    try {
      sources.push(JSON.parse(fs.readFileSync(dataSettings, "utf-8")));
    } catch {}
  }

  // Old location 2: .cf/settings.json (forge-concept)
  const cfSettings = path.join(resolved, ".cf", "settings.json");
  if (fs.existsSync(cfSettings)) {
    try {
      sources.push(JSON.parse(fs.readFileSync(cfSettings, "utf-8")));
    } catch {}
  }

  // Old location 3: .skaile/config.json (old SkailConfig)
  const oldConfig = path.join(resolved, ".skaile", "config.json");
  if (fs.existsSync(oldConfig)) {
    try {
      sources.push(JSON.parse(fs.readFileSync(oldConfig, "utf-8")));
    } catch {}
  }

  if (sources.length > 0) {
    let merged: SkaileSettings = {};
    for (const source of sources) {
      merged = mergeSettings(merged, mapLegacyFields(source as Record<string, unknown>));
    }
    await saveSettings(merged, targetPath);
  }

  _migrated.add(resolved);
}

/**
 * Resolve effective settings through the 6-layer priority chain:
 * overrides > env vars (PREFERRED_PROVIDER, PREFERRED_MODEL, *_API_KEY) >
 * .skaile/settings.json (walks upward) > ~/.skaile/settings.json >
 * skaile.yaml agent_config defaults > SETTINGS_DEFAULTS.
 *
 * @param projectDir - Root directory of the project being operated on
 * @param overrides - Optional top-priority overrides (CLI flags, API body)
 * @returns Merged effective settings
 * @docLink packages/core/settings#resolve-settings
 */
export async function resolveSettings(
  projectDir: string,
  overrides?: Partial<SkaileSettings>,
): Promise<SkaileSettings> {
  await migrateSettings(projectDir);

  // Base: built-in defaults
  let merged: SkaileSettings = { ...SETTINGS_DEFAULTS };

  // Layer: workspace config defaults (skaile.yaml via resolveSkWorkspaceConfig)
  merged = mergeSettings(merged, settingsFromWorkspaceConfig(projectDir));

  // Layer: user global settings
  const globalPath = globalSettingsPath();
  if (fs.existsSync(globalPath)) {
    try {
      const raw = JSON.parse(fs.readFileSync(globalPath, "utf-8"));
      merged = mergeSettings(merged, mapLegacyFields(raw));
    } catch {}
  }

  // Layer: project-local .skaile/settings.json (walking upward, farthest first)
  const settingsFiles = walkForSettings(projectDir);
  for (const filePath of settingsFiles.reverse()) {
    if (path.resolve(filePath) === path.resolve(globalPath)) continue;
    try {
      const raw = JSON.parse(fs.readFileSync(filePath, "utf-8"));
      merged = mergeSettings(merged, mapLegacyFields(raw));
    } catch {}
  }

  // Layer: environment variables (API keys + preferred provider/model)
  const envKeys = detectEnvApiKeys();
  if (Object.keys(envKeys).length > 0) {
    merged.apiKeys = { ...merged.apiKeys, ...envKeys };
  }
  if (process.env.PREFERRED_PROVIDER) merged.provider = process.env.PREFERRED_PROVIDER;
  if (process.env.PREFERRED_MODEL) merged.model = process.env.PREFERRED_MODEL;

  // Layer: explicit overrides
  if (overrides) merged = mergeSettings(merged, overrides);

  return merged;
}

// ── Provider / API key helpers ─────────────────────────────────────────────

/**
 * Return the environment variable name for a provider's API key.
 * e.g. `providerEnvKey('anthropic')` → `'ANTHROPIC_API_KEY'`
 *
 * @param provider - Provider identifier (e.g. "anthropic", "openai")
 * @returns Environment variable name for that provider's API key
 * @docLink packages/core/settings#provider-env-key
 */
export function providerEnvKey(provider: string): string {
  return `${provider.toUpperCase().replace(/[^A-Z0-9]/g, "_")}_API_KEY`;
}

/**
 * Return the active API key for the given provider.
 * The matching environment variable (`*_API_KEY`) takes priority over
 * the value stored in `settings.apiKeys`.
 * Applies to `cloud: default`/`gateway` only — bedrock/vertex/azure sessions
 * carry their credentials as env bundles resolved by the runner instead.
 *
 * @param provider - Provider identifier (e.g. "anthropic")
 * @param settings - Current effective settings
 * @returns API key string, or undefined if not configured
 * @docLink packages/core/settings#resolve-api-key
 */
export function resolveApiKey(provider: string, settings: SkaileSettings): string | undefined {
  return process.env[providerEnvKey(provider)] || settings.apiKeys?.[provider];
}

/**
 * Return a masked version of an API key for safe logging.
 * Shows only the last four characters (e.g. `'****abc1'`).
 *
 * @param key - Raw API key to mask
 * @returns Masked key string, or empty string if key is falsy
 * @docLink packages/core/settings#mask-api-key
 */
export function maskApiKey(key: string): string {
  return key ? `****${key.slice(-4)}` : "";
}

/**
 * An API key resolved with information about where it came from.
 * @docLink packages/core/settings#resolved-key
 */
export interface ResolvedKey {
  /** Masked API key value (last 4 chars visible). */
  key: string;
  /** Whether the key came from an environment variable or stored settings. */
  source: "env" | "stored";
}

/**
 * Scan process.env (plus any `extraEnv` overrides) for recognized API key
 * variables and return them as a partial `apiKeys` record keyed by provider id.
 *
 * @param extraEnv - Additional environment entries to merge over process.env
 * @returns Partial apiKeys record containing only providers whose key was found
 * @docLink packages/core/settings#detect-env-api-keys
 */
export function detectEnvApiKeys(extraEnv?: Record<string, string>): Record<string, string> {
  const env = { ...process.env, ...extraEnv };
  const detected: Record<string, string> = {};
  for (const provider of ALL_PROVIDERS) {
    const val = env[providerEnvKey(provider)];
    if (val) detected[provider] = val;
  }
  return detected;
}

/**
 * Build a resolved key map for all known providers with source info and masked values.
 * Env-var keys take priority over stored keys when both exist.
 *
 * @param settings - Current effective settings (provides stored apiKeys)
 * @param envKeys - Keys detected from environment (e.g. from detectEnvApiKeys())
 * @returns Map from provider id to resolved key info; omits providers with no key
 * @docLink packages/core/settings#resolve-all-keys
 */
export function resolveAllKeys(
  settings: SkaileSettings,
  envKeys: Record<string, string>,
): Record<string, ResolvedKey> {
  const result: Record<string, ResolvedKey> = {};
  for (const provider of ALL_PROVIDERS) {
    const envVal = envKeys[provider];
    const storedVal = settings.apiKeys?.[provider];
    if (envVal) {
      result[provider] = { key: maskApiKey(envVal), source: "env" };
    } else if (storedVal) {
      result[provider] = { key: maskApiKey(storedVal), source: "stored" };
    }
  }
  return result;
}
